import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { requireSession } from '@/lib/ensureSession';
import { useUserStore } from '@/store/userStore';
import { localDate } from '@/hooks/useCheckin';

// ════════════════════════════════════════════════════════════════
// Привычки рядом со здоровьем и вечер в три кнопки (миграция 046).
// «Частично» держит серию так же, как «Получилось»: человек, который
// сделал половину, не должен чувствовать провал.
// ════════════════════════════════════════════════════════════════

export type HabitStatus = 'done' | 'partial' | 'missed';

export type Habit = {
  id: string;
  title: string;
  emoji: string;
  sort_order: number;
  created_at: string;
};

export type HabitLog = {
  habit_id: string;
  day: string;
  status: HabitStatus;
  reason: string | null;
};

export const HABIT_LIMIT = 3;

// Готовые привычки — рядом со здоровьем, конкретные и проверяемые вечером
// одним ответом. «Питаться правильно» сюда не годится: вечером непонятно,
// получилось или нет.
export const HABIT_PRESETS: { title: string; emoji: string }[] = [
  { title: 'Выпить 1,5 л воды', emoji: '💧' },
  { title: 'Лечь до 23:00', emoji: '😴' },
  { title: 'Прогулка 30 минут', emoji: '🚶' },
  { title: 'Овощи в каждом приёме', emoji: '🥗' },
  { title: 'Есть без телефона', emoji: '📵' },
  { title: '5 минут тишины перед сном', emoji: '🌙' },
];

export const MISS_REASONS = ['Устал(а)', 'Не было времени', 'Забыл(а)', 'Не было настроения', 'Другое'];

// Отметок немного (до трёх в день), а вехи идут до 100 дней — берём с запасом.
const HISTORY_DAYS = 400;
export const STRIP_DAYS = 21;
// Вехи по НАКОПЛЕННЫМ дням (как в «Твоём пути»): 66 — в среднем столько нужно,
// чтобы привычка стала автоматической (Лалли, 2009).
export const HABIT_MILESTONES = [7, 21, 30, 66, 100];

// Фразы Дофи на вехах — ими человек и делится. «Дней», а не «дней подряд»:
// считаются все дни, когда получилось, пропуск не обнуляет (анти-стыд).
export const HABIT_LINES: Record<number, string> = {
  7: 'Неделя. Это уже не случайность, а начало привычки.',
  21: 'Три недели. Привычка начинает держаться сама, без уговоров.',
  30: 'Месяц. Ты делаешь это не потому что надо, а потому что так живёшь.',
  66: '66 дней — в среднем столько нужно, чтобы привычка стала автоматической. Ты здесь.',
  100: 'Сто дней. Это уже не привычка, а часть тебя.',
};

export function useHabits() {
  const { profile } = useUserStore();
  const [habits, setHabits] = useState<Habit[]>([]);
  const [logs, setLogs] = useState<HabitLog[]>([]);
  const [loaded, setLoaded] = useState(false);

  const today = localDate();

  const load = useCallback(async () => {
    if (!profile?.id) return;
    const since = localDate(-HISTORY_DAYS);
    const [h, l] = await Promise.all([
      supabase
        .from('habits')
        .select('id,title,emoji,sort_order,created_at')
        .eq('user_id', profile.id)
        .eq('is_active', true)
        .order('sort_order')
        .order('created_at'),
      supabase
        .from('habit_logs')
        .select('habit_id,day,status,reason')
        .eq('user_id', profile.id)
        .gte('day', since),
    ]);
    if (h.error) console.error('[useHabits] habits', h.error);
    if (l.error) console.error('[useHabits] logs', l.error);
    setHabits((h.data as Habit[]) ?? []);
    setLogs((l.data as HabitLog[]) ?? []);
    setLoaded(true);
  }, [profile?.id]);

  useEffect(() => { load(); }, [load]);

  async function addHabit(title: string, emoji: string) {
    if (!profile?.id || habits.length >= HABIT_LIMIT) return false;
    if (!(await requireSession())) return false;
    const { data, error } = await supabase
      .from('habits')
      .insert({ user_id: profile.id, title: title.trim().slice(0, 80), emoji, sort_order: habits.length })
      .select('id,title,emoji,sort_order,created_at')
      .single();
    if (error) { console.error('[useHabits] add', error); return false; }
    setHabits((hs) => [...hs, data as Habit]);
    return true;
  }

  // Убираем мягко: история отметок остаётся, привычку можно будет вернуть.
  async function archiveHabit(id: string) {
    if (!(await requireSession())) return;
    const { error } = await supabase
      .from('habits')
      .update({ is_active: false, archived_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { console.error('[useHabits] archive', error); return; }
    setHabits((hs) => hs.filter((h) => h.id !== id));
  }

  async function mark(habitId: string, status: HabitStatus, reason: string | null = null) {
    if (!profile?.id) return;
    if (!(await requireSession())) return;
    const prev = logs;
    const next: HabitLog = { habit_id: habitId, day: today, status, reason: status === 'missed' ? reason : null };
    setLogs((ls) => [...ls.filter((x) => !(x.habit_id === habitId && x.day === today)), next]);
    const { error } = await supabase
      .from('habit_logs')
      .upsert(
        { user_id: profile.id, habit_id: habitId, day: today, status, reason: next.reason, updated_at: new Date().toISOString() },
        { onConflict: 'habit_id,day' },
      );
    if (error) { console.error('[useHabits] mark', error); setLogs(prev); }
  }

  function todayLog(habitId: string): HabitLog | undefined {
    return logs.find((l) => l.habit_id === habitId && l.day === today);
  }

  // Серия: дни подряд с «Получилось» или «Частично». Сегодня без отметки серию
  // не рвёт — день ещё не закончился, считаем от вчера.
  function streak(habitId: string): number {
    const kept = new Set(logs.filter((l) => l.habit_id === habitId && l.status !== 'missed').map((l) => l.day));
    let n = 0;
    let offset = kept.has(today) ? 0 : -1;
    while (kept.has(localDate(offset))) { n++; offset--; }
    return n;
  }

  // Полоска «как в Твоём пути»: последние дни с момента, когда привычка заведена
  // (не больше STRIP_DAYS), от старых к сегодняшнему.
  function history(habit: Habit): { day: string; status: HabitStatus | null }[] {
    const created = habit.created_at ? habit.created_at.slice(0, 10) : today;
    const out: { day: string; status: HabitStatus | null }[] = [];
    for (let off = -(STRIP_DAYS - 1); off <= 0; off++) {
      const d = localDate(off);
      if (d < created) continue;
      const l = logs.find((x) => x.habit_id === habit.id && x.day === d);
      out.push({ day: d, status: l ? l.status : null });
    }
    return out;
  }

  // Сколько всего дней получилось (done или partial) — по ним считаются вехи.
  function keptCount(habitId: string): number {
    return logs.filter((l) => l.habit_id === habitId && l.status !== 'missed').length;
  }

  return { habits, loaded, addHabit, archiveHabit, mark, todayLog, streak, history, keptCount, today, reload: load };
}
